import {
  Alert,
  Card,
  Pagination,
  SkeletonLoader,
  Table,
} from '@yoast/ui-library';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  DotsHorizontalIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import type { RankScoreListItem } from '../../api/generated/insights.schemas';
import GradientText from '../../components/GradientText';
import TitleWithTooltip from '../../components/TitleWithTooltip';
import useScan from '../../hooks/useScan';
import { getDeltaTextClassNames } from '../../utils/getDeltaTextClassNames';

interface CompetitorRankingRowProps {
  data?: RankScoreListItem;
  totalQueries?: number;
  isFirstScan?: boolean;
  isLoading?: boolean;
}

const CompetitorRankingRow = ({
  data,
  totalQueries,
  isFirstScan = true,
  isLoading = false,
}: CompetitorRankingRowProps) => {
  const showSkeleton = isLoading || !data;
  const { rank, brand } = data || {};

  const previousRank = rank?.previous;
  // Lower rank is better, so negative difference is good.
  const rankDelta =
    typeof previousRank?.difference === 'number'
      ? previousRank!.difference * -1
      : undefined;

  const numberOfMentions = brand?.numberOfMentions;
  const previousNrOfMentions = numberOfMentions?.previous;
  // More mentions is better, so positive difference is good.
  const nrOfMentionsDelta =
    typeof previousNrOfMentions?.difference === 'number'
      ? previousNrOfMentions!.difference
      : undefined;

  return (
    <Table.Row key={showSkeleton ? undefined : rank?.value}>
      <Table.Cell>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-8" />
        ) : (
          <span className="yst-flex yst-justify-between yst-gap-4">
            <span className="yst-flex yst-flex-nowrap yst-gap-2 yst-font-medium">
              <span className="yst-text-slate-500">#</span>
              <span className="yst-text-slate-900">{rank?.value}</span>
            </span>

            {!isFirstScan && (
              <span
                className={classNames(
                  'yst-inline-flex yst-items-center yst-justify-end',
                  getDeltaTextClassNames(
                    previousRank && typeof rankDelta === 'number'
                      ? rankDelta
                      : 0,
                  ),
                )}
              >
                {previousRank && typeof rankDelta === 'number' ? (
                  <>
                    {Math.abs(rankDelta)}
                    {rankDelta > 0 ? (
                      <ChevronUpIcon className="yst-h-4 yst-w-4" />
                    ) : (
                      <ChevronDownIcon className="yst-h-4 yst-w-4" />
                    )}
                  </>
                ) : (
                  <DotsHorizontalIcon className="yst-mx-1 yst-h-4 yst-w-4" />
                )}
              </span>
            )}
          </span>
        )}
      </Table.Cell>

      <Table.Cell>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-24" />
        ) : (
          <span className="yst-font-medium">
            {brand?.isSelf ? (
              <GradientText>{brand?.name}</GradientText>
            ) : (
              (brand?.name ?? '—')
            )}
          </span>
        )}
      </Table.Cell>

      <Table.Cell>
        {showSkeleton ? (
          <SkeletonLoader className="yst-h-6 yst-w-8" />
        ) : (
          <span className="yst-flex yst-justify-end yst-gap-4">
            <span className="yst-flex yst-flex-nowrap yst-gap-1 yst-font-medium">
              <span className="yst-text-slate-500">
                {numberOfMentions?.value}
              </span>
              <span className="yst-text-slate-900">/ {totalQueries}</span>
            </span>

            {!isFirstScan && (
              <span
                className={classNames(
                  'yst-ml-auto',
                  getDeltaTextClassNames(
                    previousNrOfMentions &&
                      typeof nrOfMentionsDelta === 'number'
                      ? nrOfMentionsDelta
                      : 0,
                  ),
                )}
              >
                {previousNrOfMentions &&
                typeof nrOfMentionsDelta === 'number' ? (
                  <>
                    {nrOfMentionsDelta > -1 && '+'}
                    {nrOfMentionsDelta}
                  </>
                ) : (
                  <DotsHorizontalIcon className="yst-h-4 yst-w-4" />
                )}
              </span>
            )}
          </span>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const CompetitorRankingCard = ({ className }: { className?: string }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: scan, isLoading, isRefetching } = useScan();
  const rankingsList = scan?.rankings?.list;

  const standardizedRankingScore = scan?.scores?.standardized?.ranking || {};
  const hasPreviousRanking = Object.hasOwn(
    standardizedRankingScore,
    'previous',
  );

  const itemsPerPage = 5;
  const totalItems = rankingsList?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = rankingsList?.slice(startIndex, endIndex);

  // Reset to page 1 when data changes or when current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage, setCurrentPage]);

  return (
    <Card ref={cardRef} className={cx('yst-overflow-visible', className)}>
      <Card.Content className="yst-mt-0 yst-space-y-6">
        <div className="yst-space-y-2">
          <TitleWithTooltip
            title="Competitor ranking"
            // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=2-5#1353634062
            tooltip="How often competitors are mentioned in your submitted queries."
          />
          <p>Brands with the most mentions for your prompts.</p>
        </div>

        {!isLoading &&
        !isRefetching &&
        (!rankingsList || rankingsList.length === 0) ? (
          <Alert variant="info">
            The selected AI model returned no brands for your queries. This can
            happen with some queries or models.
          </Alert>
        ) : (
          <Table variant="minimal">
            <Table.Head>
              <Table.Row>
                <Table.Cell className="yst-w-20 yst-font-semibold">
                  Rank
                </Table.Cell>
                <Table.Cell className="yst-font-semibold">Brand</Table.Cell>
                <Table.Cell className="yst-w-20 yst-text-right yst-font-semibold">
                  Mentions
                </Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {currentPageItems &&
              Array.isArray(currentPageItems) &&
              currentPageItems.length > 0
                ? currentPageItems.map((rankingData) => (
                    <CompetitorRankingRow
                      key={`rank-row-${rankingData.rank?.value}`}
                      data={rankingData}
                      totalQueries={scan?.numberOfQueries}
                      isFirstScan={!hasPreviousRanking}
                    />
                  ))
                : isLoading || isRefetching
                  ? // Show 5 skeleton ranking rows when loading
                    Array.from({ length: itemsPerPage }).map((_, index) => (
                      <CompetitorRankingRow
                        key={`skeleton-${index}`}
                        isLoading={true}
                      />
                    ))
                  : null}
            </Table.Body>
          </Table>
        )}

        {/* Show pagination only if there are more than itemsPerPage items */}
        {!isLoading &&
          !isRefetching &&
          rankingsList &&
          rankingsList.length > itemsPerPage && (
            <Pagination
              current={currentPage}
              total={totalPages}
              className="yst-flex-row"
              onNavigate={setCurrentPage}
              screenReaderTextNext="Next"
              screenReaderTextPrevious="Previous"
              variant="text"
            />
          )}
      </Card.Content>
    </Card>
  );
};

export default CompetitorRankingCard;
